// =============================================================================
// YsentinelDataContext — single source of truth for every Ysentinel dashboard.
//
// Provider responsibilities:
//   - Hold the currently-selected TimeRange.
//   - Hold a per-dashboard data slice that conforms to ./schema.js.
//   - Expose granular setters (setActivity, setStorage, …) and a bulk setAll
//     for the SDK; provide convenient read access via the `useYsentinel` hook.
//   - In dev / preview, reseed defaults from mockData when range changes so
//     the UI behaves like a real SDK push without any backend wired up.
//
// SDK integration: see YSENTINEL_DATA_API.md next to this file.
// =============================================================================

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { getDefaultData, DEFAULT_RANGE } from "./defaults";

// React context holding the active YsentinelData payload + setters.
// Shape documented in ./schema.js and YSENTINEL_DATA_API.md.
const YsentinelDataContext = createContext(null);

// Provider props (see ./schema.js for the full payload shape):
//   children      — React subtree
//   initialRange  — TimeRange string ("24h" | "7d" | "14d" | "30d") or
//                   { type: "custom", startDate, endDate }
//   initialData   — Partial<YsentinelData>; when supplied (e.g. by the SDK in
//                   production), seeds the provider with real data and
//                   disables the mock reseed-on-range-change behavior.
//   navigate      — (target: "files"|"users"|"locations"|"summary"|"analysis") => void
//                   used by goToDashboard so cards can pivot to a sibling tab
//                   (e.g. UsersDashboard's FilesTouchedCard click → Files tab).
//                   Optional: provider degrades to a no-op when omitted.
export const YsentinelDataProvider = ({ children, initialRange = DEFAULT_RANGE, initialData, navigate }) => {
  const [range, setRange] = useState(initialRange);

  // `seedFromMock` toggles the dev-mode behavior: when true, range changes
  // re-derive data from mockData; when false (i.e. SDK has pushed real data),
  // range changes only update state and the SDK is expected to refetch.
  const [seedFromMock, setSeedFromMock] = useState(!initialData);

  const [overview, setOverviewState]   = useState(() => initialData?.overview  ?? getDefaultData(initialRange).overview);
  const [files, setFilesState]         = useState(() => initialData?.files     ?? getDefaultData(initialRange).files);
  const [users, setUsersState]         = useState(() => initialData?.users     ?? getDefaultData(initialRange).users);
  const [locations, setLocationsState] = useState(() => initialData?.locations ?? getDefaultData(initialRange).locations);
  const [analysis, setAnalysisState]   = useState(() => initialData?.analysis  ?? getDefaultData(initialRange).analysis);

  // Dev mode: range change → reseed mock. Production (SDK-fed) skips this and
  // relies on the SDK to call setAll with fresh data after setRange.
  //
  // Selection state (which file / which subject the analyst has picked, plus
  // breadcrumbs and recents) is *user-driven* and must survive the reseed —
  // otherwise switching the time range would punt the user back to the empty
  // picker even though the data underneath the picker is just being scoped.
  useEffect(() => {
    if (!seedFromMock) return;
    const next = getDefaultData(range);
    setOverviewState(next.overview);
    setFilesState((prev) => ({
      ...next.files,
      selectedFileUuid: prev?.selectedFileUuid ?? next.files.selectedFileUuid,
      breadcrumb:       prev?.breadcrumb       ?? next.files.breadcrumb,
    }));
    setUsersState((prev) => ({
      ...next.users,
      selectedSubject:      prev?.selectedSubject      ?? next.users.selectedSubject,
      selectedSubjectStack: prev?.selectedSubjectStack ?? next.users.selectedSubjectStack,
      recentSubjects:       prev?.recentSubjects       ?? next.users.recentSubjects,
    }));
    setLocationsState(next.locations);
    setAnalysisState(next.analysis);
  }, [range, seedFromMock]);

  // ── Bulk setters ──────────────────────────────────────────────────────────

  // Replace every slice at once. Most common SDK call.
  const setAll = useCallback((payload) => {
    setSeedFromMock(false);
    if (payload?.range) setRange(payload.range);
    if (payload?.overview)  setOverviewState(payload.overview);
    if (payload?.files)     setFilesState(payload.files);
    if (payload?.users)     setUsersState(payload.users);
    if (payload?.locations) setLocationsState(payload.locations);
    if (payload?.analysis)  setAnalysisState(payload.analysis);
  }, []);

  // Replace one dashboard slice at a time. Useful for incremental updates.
  const setOverview  = useCallback((next) => { setSeedFromMock(false); setOverviewState(next); }, []);
  const setFiles     = useCallback((next) => { setSeedFromMock(false); setFilesState(next); }, []);
  const setUsers     = useCallback((next) => { setSeedFromMock(false); setUsersState(next); }, []);
  const setLocations = useCallback((next) => { setSeedFromMock(false); setLocationsState(next); }, []);
  const setAnalysis  = useCallback((next) => { setSeedFromMock(false); setAnalysisState(next); }, []);

  // ── Granular Overview setters ─────────────────────────────────────────────
  // Common case: SDK pushes new data for one panel without re-sending the
  // whole snapshot. Each setter merges into the existing overview slice.
  const patchOverview = useCallback((patch) => {
    setSeedFromMock(false);
    setOverviewState((prev) => ({ ...prev, ...patch }));
  }, []);

  const overviewSetters = useMemo(() => ({
    setSnapshot:                 setOverview,
    setActivity:                 (activity)                => patchOverview({ activity }),
    setStorage:                  (storage)                 => patchOverview({ storage }),
    setPrivilegedDaily:          (privilegedDaily)         => patchOverview({ privilegedDaily }),
    setExternalShareFlow:        (externalShareFlow)       => patchOverview({ externalShareFlow }),
    setTopMovers:                (topMovers)               => patchOverview({ topMovers }),
    setExternalShareRankByUser:  (externalShareRankByUser) => patchOverview({ externalShareRankByUser }),
    setExternalShareRankByTeam:  (externalShareRankByTeam) => patchOverview({ externalShareRankByTeam }),
    setLocations:                (locations)               => patchOverview({ locations }),
    setRecentActivity:           (recentActivity)          => patchOverview({ recentActivity }),
    setOffHoursRatio:            (offHoursRatio)           => patchOverview({ offHoursRatio }),
    setDeniedDaily:              (deniedDaily)             => patchOverview({ deniedDaily }),
    setTopOperations:            (topOperations)           => patchOverview({ topOperations }),
    setKpis:                     (kpis)                    => patchOverview({ kpis }),
    setOrg:                      (org)                     => patchOverview({ org }),
  }), [setOverview, patchOverview]);

  // ── Memoized public value ─────────────────────────────────────────────────

  // Granular Files setters — used by the Files dashboard to drive the picker
  // and breadcrumb without replacing the whole files slice.
  const patchFiles = useCallback((patch) => {
    setSeedFromMock(false);
    setFilesState((prev) => ({ ...prev, ...patch }));
  }, []);

  const filesSetters = useMemo(() => ({
    // Bulk
    setSnapshot:         setFiles,

    // Reference data — file metadata, folder tree, lineage graph
    setFilesList:        (arr)  => patchFiles({ files: arr }),
    setFolderTree:       (arr)  => patchFiles({ folderTree: arr }),
    setLineage:          (arr)  => patchFiles({ lineage: arr }),

    // Per-uuid records consumed by the dashboard cards
    setFileEvents:       (rec)  => patchFiles({ fileEvents: rec }),
    setCurrentShares:    (rec)  => patchFiles({ currentShares: rec }),
    setPathHistory:      (rec)  => patchFiles({ pathHistory: rec }),
    setExternalExposure: (rec)  => patchFiles({ externalExposure: rec }),

    // Selection / branch navigation
    setSelectedFile:     (uuid) => patchFiles({ selectedFileUuid: uuid, breadcrumb: uuid ? [uuid] : [] }),
    pushBranch:          (uuid) => setFilesState((prev) => ({
      ...prev,
      selectedFileUuid: uuid,
      breadcrumb: [...(prev.breadcrumb || []), uuid],
    })),
    popToBreadcrumb:     (idx)  => setFilesState((prev) => {
      const next = (prev.breadcrumb || []).slice(0, idx + 1);
      return { ...prev, breadcrumb: next, selectedFileUuid: next[next.length - 1] || null };
    }),
    clearSelection:      ()     => patchFiles({ selectedFileUuid: null, breadcrumb: [] }),
  }), [setFiles, patchFiles]);

  // ── Granular Users / Teams setters ────────────────────────────────────────
  // Mirror filesSetters: bulk reference data, granular per-key updates, and
  // selection-state navigation (selectedSubject + subject stack + recents).
  const patchUsers = useCallback((patch) => {
    setSeedFromMock(false);
    setUsersState((prev) => ({ ...prev, ...patch }));
  }, []);

  const usersSetters = useMemo(() => ({
    // Bulk
    setSnapshot:         setUsers,

    // Reference data
    setUsersList:        (arr)  => patchUsers({ users: arr }),
    setTeamsList:        (arr)  => patchUsers({ teams: arr }),
    setKnownDomains:     (arr)  => patchUsers({ knownDomains: arr }),

    // Per-key activity bundles
    setUserActivity:     (email, bundle) => setUsersState((prev) => ({
      ...prev,
      userActivity: { ...(prev.userActivity || {}), [email]: bundle },
    })),
    setTeamActivity:     (team, bundle) => setUsersState((prev) => ({
      ...prev,
      teamActivity: { ...(prev.teamActivity || {}), [team]: bundle },
    })),

    // Risk + anomalies (key = email for users, "team:<name>" for teams)
    setRiskScore:        (key, score) => setUsersState((prev) => ({
      ...prev,
      riskScores: { ...(prev.riskScores || {}), [key]: score },
    })),
    setAnomalies:        (key, list) => setUsersState((prev) => ({
      ...prev,
      anomalies: { ...(prev.anomalies || {}), [key]: list },
    })),

    // Selection / pivot navigation
    setSelectedSubject:  (ref) => setUsersState((prev) => ({
      ...prev,
      selectedSubject: ref,
      selectedSubjectStack: ref ? [ref] : [],
    })),
    pushSubject:         (ref) => setUsersState((prev) => ({
      ...prev,
      selectedSubject: ref,
      selectedSubjectStack: [...(prev.selectedSubjectStack || []), ref],
    })),
    popSubject:          (idx) => setUsersState((prev) => {
      const stack = (prev.selectedSubjectStack || []).slice(0, idx + 1);
      return {
        ...prev,
        selectedSubjectStack: stack,
        selectedSubject: stack[stack.length - 1] || null,
      };
    }),
    clearSelection:      () => patchUsers({ selectedSubject: null, selectedSubjectStack: [] }),

    // Recents (used by the picker's "Recently viewed" rail). Most-recent first,
    // max 10. Dedupes on (kind, key) so reopening a subject moves it to the top.
    pushRecentSubject:   (ref) => setUsersState((prev) => {
      const at = Date.now();
      const without = (prev.recentSubjects || []).filter(
        (r) => !(r.kind === ref.kind && r.key === ref.key),
      );
      return { ...prev, recentSubjects: [{ ...ref, at }, ...without].slice(0, 10) };
    }),
  }), [setUsers, patchUsers]);

  // Cross-dashboard navigation. Cards inside one dashboard can pivot to a
  // sibling (UsersDashboard → FilesDashboard with a file selected). The
  // provider doesn't own active-card state — that's owned by the host
  // (Homepage/index.jsx) — so we just hand the call up via the `navigate` prop.
  const goToDashboard = useCallback((target) => {
    if (typeof navigate === "function") navigate(target);
  }, [navigate]);

  const value = useMemo(() => ({
    range,
    setRange,

    setAll,
    goToDashboard,

    overview:  { ...overview,  ...overviewSetters },
    files:     { ...files,     ...filesSetters },
    users:     { ...users,     ...usersSetters },
    locations: { ...locations, setSnapshot: setLocations },
    analysis:  { ...analysis,  setSnapshot: setAnalysis },
  }), [
    range, setAll, goToDashboard,
    overview, overviewSetters,
    files, filesSetters,
    users, usersSetters,
    locations, setLocations,
    analysis, setAnalysis,
  ]);

  return (
    <YsentinelDataContext.Provider value={value}>
      {children}
    </YsentinelDataContext.Provider>
  );
};

// Read access + setters for the active Ysentinel dashboard data. Throws when
// used outside a provider, so call sites can rely on the value being present.
export const useYsentinel = () => {
  const ctx = useContext(YsentinelDataContext);
  if (!ctx) {
    throw new Error("useYsentinel must be called inside <YsentinelDataProvider>.");
  }
  return ctx;
};
